// Ficha de um pedido: conversa (com notas internas), responder com resposta guardada e imagens,
// e painel lateral com estado/prioridade/categoria/responsável, quem pediu, contexto, prazos e histórico.
import { useApi, useMe } from "@ecliptica/api-client/react";
import { CATEGORIES, PRIORITIES, STATUSES, canStaffTransition, cannedBody, staffReplyStatus, type Category, type Priority, type SlaState, type Status } from "@ecliptica/contexts-support";
import { AttachmentList, ImagePicker, SlaBadge, TicketStatusBadge, postWithFiles, supportErrorKey, type AttachmentInfo } from "@ecliptica/game-ui";
import { isLang, useTranslation } from "@ecliptica/i18n";
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, NativeSelect, Skeleton, Tabs, TabsList, TabsTrigger, Textarea, cn, toast } from "@ecliptica/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Lock } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { useCan } from "../../features/auth/RequireStaff";
import { RoleBadge, StatusBadge } from "../../features/users/badges";
import { formatDate } from "../../lib/format";

type Detail = {
  ticket: {
    id: number;
    number: string;
    subject: string;
    category: Category;
    priority: Priority;
    status: Status;
    source: string;
    locale: string;
    contactEmail: string;
    contactName: string | null;
    userId: string | null;
    assigneeId: string | null;
    meta: Record<string, unknown> | null;
    createdAt: string;
    sla: { firstResponseDue: string; resolutionDue: string; firstResponse: SlaState; resolution: SlaState; worst: SlaState };
  };
  requester: { id: string; name: string; email: string; role: "player" | "support" | "moderator" | "designer" | "admin"; status: "active" | "suspended" | "banned" | "unverified" } | null;
  thread: { id: number; authorType: string; authorName: string | null; body: string; internal: boolean; createdAt: string; attachments: AttachmentInfo[] }[];
  events: { id: number; kind: string; from: string | null; to: string | null; at: string; actorName: string | null }[];
};

function Side({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">{children}</CardContent>
    </Card>
  );
}

export function TicketDetailPage() {
  const { number = "" } = useParams();
  const { t, i18n } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const me = useMe();
  const lang = i18n.language;
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [after, setAfter] = useState<Status | "">("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const q = useQuery({
    queryKey: ["ticket", number],
    queryFn: async () => {
      const res = await api.v1.admin.support.tickets[":number"].$get({ param: { number } });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as unknown as Detail;
    },
  });
  const staff = useQuery({
    queryKey: ["support-staff"],
    queryFn: async () => (await (await api.v1.admin.support.staff.$get()).json()) as { id: string; name: string }[],
  });
  const canned = useQuery({
    queryKey: ["canned"],
    queryFn: async () => (await (await api.v1.admin.support.canned.$get()).json()) as { id: number; title: string; bodyPt: string; bodyEn: string }[],
  });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["ticket", number] });
    await qc.invalidateQueries({ queryKey: ["tickets"] });
  };

  if (q.isPending) return <Skeleton className="h-64 w-full" />;
  if (!q.data) return <EmptyState title={t("error.notFound")} />;
  const { ticket: tk, requester, thread, events } = q.data;
  const canReply = can("support.reply");

  async function patch(p: Partial<{ status: Status; priority: Priority; category: Category; assigneeId: string | null }>) {
    const res = await api.v1.admin.support.tickets[":number"].$patch({ param: { number }, json: p });
    if (!res.ok) return toast.error(t(supportErrorKey((await res.json()) as Record<string, unknown>)));
    toast.success(t("desk.detail.saved"));
    await refresh();
  }

  async function send() {
    setBusy(true);
    setError(null);
    const r = await postWithFiles(
      `/v1/admin/support/tickets/${number}/messages`,
      { body: body.trim(), internal: mode === "note" ? "true" : "false", status: mode === "reply" && after ? after : undefined },
      files,
    );
    setBusy(false);
    if (!r.ok) return setError(supportErrorKey(r.body));
    setBody("");
    setFiles([]);
    setAfter("");
    toast.success(t("desk.detail.sent"));
    await refresh();
  }

  const eventText = (e: Detail["events"][number]) => {
    const label = (v: string | null, ns: string) => (v ? t(`support.${ns}.${v}`) : "—");
    if (e.kind === "status") return t("desk.detail.events.status", { from: label(e.from, "status"), to: label(e.to, "status") });
    if (e.kind === "priority") return t("desk.detail.events.priority", { from: label(e.from, "priority"), to: label(e.to, "priority") });
    if (e.kind === "category") return t("desk.detail.events.category", { from: label(e.from, "category"), to: label(e.to, "category") });
    return t(`desk.detail.events.${e.kind}`);
  };

  return (
    <div className="flex flex-col gap-4">
      <Link to="/support" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> {t("nav.support")}
      </Link>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-extrabold text-gold">{tk.subject}</h1>
          <p className="text-sm text-muted-foreground">
            {tk.number} · {t(`support.category.${tk.category}`)} · {formatDate(tk.createdAt, lang)}
          </p>
        </div>
        <TicketStatusBadge status={tk.status} />
        <SlaBadge state={tk.sla.worst} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_20rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <ol className="flex flex-col gap-3">
            {thread.map((m) => (
              <li
                key={m.id}
                className={cn(
                  "rounded-lg border px-4 py-3",
                  m.internal ? "border-dashed border-warning/50 bg-warning/5" : m.authorType === "staff" ? "border-gold/30 bg-indigo/30" : "bg-muted/60",
                )}
              >
                <p className="mb-1 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                  {m.internal && <Lock className="size-3 text-warning" />}
                  {m.authorType === "staff" ? (m.authorName ?? "—") : (requester?.name ?? tk.contactName ?? tk.contactEmail)}
                  {m.internal && <span className="text-warning">· {t("desk.detail.internal")}</span>} · {formatDate(m.createdAt, lang)}
                </p>
                <p className="whitespace-pre-wrap text-sm">{m.body}</p>
                <AttachmentList items={m.attachments} />
              </li>
            ))}
          </ol>

          {canReply && (
            <Card>
              <CardContent className="flex flex-col gap-3 p-4">
                <Tabs value={mode} onValueChange={(v) => setMode(v as "reply" | "note")}>
                  <TabsList>
                    <TabsTrigger value="reply">{t("desk.detail.reply")}</TabsTrigger>
                    <TabsTrigger value="note">
                      <Lock className="mr-1 size-3" /> {t("desk.detail.note")}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                {mode === "reply" && (canned.data?.length ?? 0) > 0 && (
                  <NativeSelect
                    aria-label={t("desk.detail.insertCanned")}
                    value=""
                    onChange={(e) => {
                      const c = canned.data!.find((x) => String(x.id) === e.target.value);
                      if (c) setBody((b) => (b ? `${b}\n\n` : "") + cannedBody(c, tk.locale));
                    }}
                  >
                    <option value="">{t("desk.detail.insertCanned")}…</option>
                    {canned.data!.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </NativeSelect>
                )}
                <Textarea
                  className={cn("min-h-28", mode === "note" && "border-warning/50")}
                  maxLength={8000}
                  placeholder={t(mode === "reply" ? "desk.detail.placeholderReply" : "desk.detail.placeholderNote")}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
                <ImagePicker files={files} onChange={setFiles} />
                {error && <Alert variant="destructive">{t(error)}</Alert>}
                <div className="flex flex-wrap items-center gap-2">
                  {mode === "reply" && (
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      {t("desk.detail.statusAfter")}
                      <NativeSelect value={after} onChange={(e) => setAfter(e.target.value as Status | "")}>
                        <option value="">{t(`support.status.${staffReplyStatus(tk.status)}`)}</option>
                        {(["in_progress", "resolved", "closed"] as const)
                          .filter((s) => canStaffTransition(tk.status, s))
                          .map((s) => (
                            <option key={s} value={s}>
                              {t(`support.status.${s}`)}
                            </option>
                          ))}
                      </NativeSelect>
                    </label>
                  )}
                  <Button className="ml-auto" variant={mode === "note" ? "outline" : "primary"} disabled={busy || !body.trim()} onClick={() => void send()}>
                    {t("desk.detail.send")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <Side title={t("desk.detail.details")}>
            {(
              [
                ["status", STATUSES.filter((s) => canStaffTransition(tk.status, s)), tk.status, "status"],
                ["priority", PRIORITIES, tk.priority, "priority"],
                ["category", CATEGORIES, tk.category, "category"],
              ] as const
            ).map(([key, options, value, ns]) => (
              <label key={key} className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{t(`desk.detail.${key}`)}</span>
                <NativeSelect className="h-8 w-40" disabled={!canReply} value={value} onChange={(e) => void patch({ [key]: e.target.value })}>
                  {options.map((o) => (
                    <option key={o} value={o}>
                      {t(`support.${ns}.${o}`)}
                    </option>
                  ))}
                </NativeSelect>
              </label>
            ))}
            <label className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">{t("desk.detail.assignee")}</span>
              <NativeSelect className="h-8 w-40" disabled={!canReply} value={tk.assigneeId ?? ""} onChange={(e) => void patch({ assigneeId: e.target.value || null })}>
                <option value="">{t("desk.detail.unassigned")}</option>
                {(staff.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </NativeSelect>
            </label>
            {canReply && me.data && tk.assigneeId !== me.data.user.id && (
              <Button size="sm" variant="outline" onClick={() => void patch({ assigneeId: me.data!.user.id })}>
                {t("desk.detail.takeIt")}
              </Button>
            )}
          </Side>

          <Side title={t("desk.detail.requester")}>
            {requester ? (
              <>
                <p className="font-semibold">{requester.name}</p>
                <p className="text-muted-foreground">{requester.email}</p>
                <div className="flex gap-2">
                  <RoleBadge role={requester.role} />
                  <StatusBadge status={requester.status} />
                </div>
                {can("users.read") && (
                  <div className="flex gap-2">
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/users/${requester.id}`}>{t("desk.detail.openUser")}</Link>
                    </Button>
                    {can("users.compensate") && (
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/users/${requester.id}?act=compensate`}>{t("desk.detail.compensate")}</Link>
                      </Button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="font-semibold">{tk.contactName ?? "—"}</p>
                <p className="text-muted-foreground">{tk.contactEmail}</p>
                <p className="text-xs text-warning">{t("desk.detail.noAccount")}</p>
              </>
            )}
          </Side>

          <Side title={t("desk.detail.deadlines")}>
            <p className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">{t("desk.detail.firstResponse")}</span>
              <span className="text-right">
                {formatDate(tk.sla.firstResponseDue, lang)} <SlaBadge state={tk.sla.firstResponse} />
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">{t("desk.detail.resolution")}</span>
              <span className="text-right">
                {formatDate(tk.sla.resolutionDue, lang)} <SlaBadge state={tk.sla.resolution} />
              </span>
            </p>
          </Side>

          <Side title={t("desk.detail.context")}>
            <p>
              <span className="text-muted-foreground">{t("desk.detail.source")}:</span> {tk.source} · {isLang(tk.locale) ? t(`languages.${tk.locale}`) : tk.locale}
            </p>
            {tk.meta &&
              Object.entries(tk.meta)
                .filter(([, v]) => v !== null && v !== "")
                .map(([k, v]) => (
                  <p key={k} className="break-words font-mono text-xs">
                    <span className="text-muted-foreground">{k}:</span> {typeof v === "string" ? v : JSON.stringify(v)}
                  </p>
                ))}
          </Side>

          <Side title={t("desk.detail.timeline")}>
            <ol className="flex flex-col gap-1.5">
              {events.map((e) => (
                <li key={e.id} className="text-xs">
                  <span className="font-semibold">{eventText(e)}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {e.actorName ?? "—"} · {formatDate(e.at, lang)}
                  </span>
                </li>
              ))}
            </ol>
          </Side>
        </div>
      </div>
    </div>
  );
}
